import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { ApiError } from "../../../shared/api/client";
import { AppButton, AppField, AppInput } from "../../../shared/components/ui";
import { GoogleSignInButton } from "../GoogleSignInButton";
import { googleLogin, login, mfaEnroll, mfaVerify, type LoginResponse } from "../api";
import { setSession, type AuthUser } from "../session";
import { homePathForRole } from "../homePath";
import { api } from "../../../shared/api/client";

type AuthStage = "login" | "mfa" | "pending";
type MfaSetupTab = "qr" | "manual";

/**
 * Production login — password + Google Identity Services, MFA with QR or setup key.
 */
export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<AuthStage>("login");
  const [challengeToken, setChallengeToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [setupTab, setSetupTab] = useState<MfaSetupTab>("qr");
  const [pendingMessage, setPendingMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const busyRef = useRef(false);

  const completeLogin = useCallback(
    async (token: string, user?: AuthUser) => {
      let profile = user;
      setSession(token, profile);
      // Always fetch /auth/me to ensure permissions and scope fields are current.
      try {
        const me = await api.get<AuthUser>("/auth/me");
        profile = { ...profile, ...me };
        setSession(token, profile);
      } catch {
        /* keep partial profile — session is still valid */
      }
      const fallback = homePathForRole(profile?.role);
      navigate(from && from !== "/login" ? from : fallback, { replace: true });
    },
    [from, navigate],
  );

  const handleAuthResponse = useCallback(
    async (res: LoginResponse) => {
      if (res.token) {
        await completeLogin(res.token, res.user);
        return;
      }

      if (res.status === "pending_approval") {
        setStage("pending");
        setPendingMessage(res.message ?? "Your account is awaiting admin approval.");
        return;
      }

      if (
        (res.status === "mfa_required" || res.status === "mfa_enrollment_required") &&
        res.challenge_token
      ) {
        setChallengeToken(res.challenge_token);
        setStage("mfa");
        setOtpCode("");
        setMfaSecret("");
        setOtpauthUri("");
        setQrDataUrl("");
        setSetupTab("qr");
        if (res.status === "mfa_enrollment_required") {
          const enroll = await mfaEnroll(res.challenge_token);
          setMfaSecret(enroll.secret);
          setOtpauthUri(enroll.otpauth_uri);
        }
        return;
      }

      throw new Error("Unexpected login response");
    },
    [completeLogin],
  );

  useEffect(() => {
    if (!otpauthUri) {
      setQrDataUrl("");
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(otpauthUri, {
      width: 220,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#0f172a", light: "#ffffff" },
    }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busyRef.current) return;
    setError(null);
    if (!username.trim() || !password) {
      setError("Enter your username and password.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    try {
      const res = await login(username.trim(), password);
      await handleAuthResponse(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Wrong username or password — try again");
      } else if (err instanceof ApiError && err.status === 403) {
        setError("Account pending approval or inactive.");
      } else {
        setError("Can't reach the server right now. Check your connection and try again.");
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const onGoogleCredential = useCallback(
    async (idToken: string) => {
      if (busyRef.current) return;
      const code = companyCode.trim().toUpperCase();
      if (!code) {
        setError("Enter your company code before continuing with Google (needed for new account requests).");
        return;
      }
      busyRef.current = true;
      setBusy(true);
      setError(null);
      try {
        const res = await googleLogin(idToken, code);
        await handleAuthResponse(res);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message || "Google sign-in failed.");
        } else {
          setError(err instanceof Error ? err.message : "Google sign-in failed.");
        }
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [companyCode, handleAuthResponse],
  );

  const onGoogleError = useCallback((message: string) => {
    setError(message);
  }, []);

  async function onVerifyMfa(e: React.FormEvent) {
    e.preventDefault();
    if (busyRef.current || !challengeToken) return;
    if (!otpCode.trim()) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await mfaVerify(challengeToken, otpCode.trim());
      await completeLogin(res.token, res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid verification code.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function copySetupKey() {
    if (!mfaSecret) return;
    try {
      await navigator.clipboard.writeText(mfaSecret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Could not copy setup key — select and copy it manually.");
    }
  }

  const enrolling = Boolean(mfaSecret);

  return (
    <div className="pg-login">
      <div className="pg-login-card">
        <div className="pg-login-brand">
          <span className="pg-login-sun" aria-hidden="true" />
          PAY-GO <strong>Africa</strong>
        </div>
        <p className="pg-login-tagline">Sign in to PAY-GO Africa</p>

        {stage === "login" && (
          <>
            <form onSubmit={onSubmit} noValidate>
              <AppField label="Username">
                <AppInput
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  autoFocus
                  placeholder="Your username"
                />
              </AppField>
              <AppField label="Password">
                <AppInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Your password"
                />
              </AppField>

              <AppButton type="submit" disabled={busy} className="pg-login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </AppButton>
            </form>

            <div className="pg-login-divider" role="separator">
              <span>or</span>
            </div>

            <AppField label="Company code (for Google sign-up)">
              <AppInput
                value={companyCode}
                onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                autoCapitalize="characters"
                autoComplete="organization"
                placeholder="e.g. GREENLEAF"
                disabled={busy}
              />
            </AppField>
            <p className="pg-muted" style={{ marginTop: -8, marginBottom: 12, fontSize: 13 }}>
              Existing Google users can sign in with any code; new requests route to that company&apos;s Business Admin.
            </p>

            <GoogleSignInButton
              disabled={busy}
              onCredential={onGoogleCredential}
              onError={onGoogleError}
            />
          </>
        )}

        {stage === "mfa" && (
          <form onSubmit={onVerifyMfa} noValidate>
            {enrolling ? (
              <div className="pg-mfa-setup">
                <h2 className="pg-mfa-title">Set up authenticator</h2>
                <p className="pg-mfa-lead">
                  Add PAY-GO to your preferred authenticator app (Google Authenticator, Microsoft
                  Authenticator, Authy, 1Password, etc.), then enter the 6-digit code it shows.
                </p>

                <div className="pg-mfa-tabs" role="tablist" aria-label="Authenticator setup method">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={setupTab === "qr"}
                    className={setupTab === "qr" ? "is-active" : undefined}
                    onClick={() => setSetupTab("qr")}
                  >
                    Scan QR code
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={setupTab === "manual"}
                    className={setupTab === "manual" ? "is-active" : undefined}
                    onClick={() => setSetupTab("manual")}
                  >
                    Enter setup key
                  </button>
                </div>

                {setupTab === "qr" ? (
                  <div className="pg-mfa-panel" role="tabpanel">
                    {qrDataUrl ? (
                      <img
                        className="pg-mfa-qr"
                        src={qrDataUrl}
                        alt="QR code to scan with your authenticator app"
                        width={220}
                        height={220}
                      />
                    ) : (
                      <p className="pg-muted">Preparing QR code…</p>
                    )}
                    <p className="pg-mfa-hint">Open your authenticator app and scan this barcode.</p>
                  </div>
                ) : (
                  <div className="pg-mfa-panel" role="tabpanel">
                    <p className="pg-mfa-hint">
                      Choose “Enter a setup key” (or similar) in your app, then paste this key:
                    </p>
                    <code className="pg-mfa-secret">{mfaSecret}</code>
                    <AppButton type="button" variant="secondary" onClick={copySetupKey}>
                      {copied ? "Copied" : "Copy setup key"}
                    </AppButton>
                    <p className="pg-mfa-hint">Account type: time-based (TOTP) · 6 digits · 30 seconds</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="pg-mfa-lead">Enter the 6-digit code from your authenticator app.</p>
            )}

            <AppField label="Authenticator code">
              <AppInput
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                autoFocus={!enrolling}
                maxLength={6}
              />
            </AppField>
            <AppButton type="submit" disabled={busy} className="pg-login-submit">
              {busy ? "Verifying…" : "Verify & sign in"}
            </AppButton>
            <button
              type="button"
              className="pg-login-text-btn"
              onClick={() => {
                setStage("login");
                setChallengeToken("");
                setMfaSecret("");
                setOtpauthUri("");
                setError(null);
              }}
            >
              Back to sign in
            </button>
          </form>
        )}

        {stage === "pending" && (
          <div className="pg-login-notice">
            <p className="pg-login-notice-title">{pendingMessage || "Account pending approval"}</p>
            <p className="pg-muted">
              An admin must approve your role and access level before you can continue.
            </p>
            <button type="button" className="pg-login-text-btn" onClick={() => setStage("login")}>
              Back to sign in
            </button>
          </div>
        )}

        {error ? (
          <p className="pg-login-error" role="alert">
            {error}
          </p>
        ) : null}

        <p className="pg-login-backlink">
          <a href="/" target="_blank" rel="noreferrer">
            Back to Website
          </a>
        </p>
      </div>
    </div>
  );
}
